import React, { useMemo, useState } from "react";
import axios from "axios";
import { THEME, flexxState, flexxEvents, awardXP } from "./state";
import ContextGate from "./ContextGate";
import SessionsClock from "./SessionsClock";
import { getSessionState } from "./flexxTime";
import SessionCompass, { evaluateCompass } from "./SessionCompass";

const EMOTIONS = ["Confident", "Neutral", "Anxious", "FOMO", "Revenge"];
const RESULTS = ["Win", "Loss", "Break Even"];
const DIRECTIONS = ["Long", "Short"];
const SESSIONS = ["Tokyo", "London", "New York", "Overlap"];
const TIMEFRAMES = ["M15", "H1", "H4", "D1"];
const SCAN_QUALITY = ["A-grade", "B-grade", "C-grade"];
const EXECUTION_QUALITY = ["At plan", "Slightly off", "Chased entry"];

// SuPER 2.0 — only these 5 setups are valid FLEXX trades.
const SETUP_TYPES = [
  "Liquidity Sweep + Reversal",
  "Order Block Retest",
  "Fair Value Gap Fill",
  "Breakout + Retest",
  "Trend Continuation",
];

// 7 SuPER Understand questions — Section 3 of the build spec
const U_QUESTIONS = [
  "D1/W1 bias is CLEAR and CONFIRMED",
  "No major news event in next 24 hours (or accounted for)",
  "Session context supports this trade type",
  "DXY is confirming (Forex) / BTC Dom is aligned (Crypto)",
  "Overall sentiment supports my direction",
  "HTF market structure supports this entry",
  "I can explain WHY this trade makes sense in one sentence",
];

// R¹ — Active risk-management checklist (open trades). Spec: super_2_system.md → R¹.
const R1_CHECKLIST = [
  "I have NOT moved SL further from entry",
  "I have NOT added to a losing position",
  "I have NOT removed TP because 'could go further'",
  "I am following my If-Then rules from the plan",
  "I am NOT down -2% on the day yet (circuit breaker)",
];

const API = process.env.REACT_APP_BACKEND_URL;

// ── Pip / point conventions per asset ──────────────────────────────────────
// Same source-of-truth as AxelResearchDesk (see XAUUSD: 1 pip=0.10, 1 point=0.01).
// All journal pip/point displays MUST use this table — never label raw price diff as pips.
const PIP_POINT_SIZE = {
  XAUUSD: { pip: 0.10, point: 0.01  },
  BTCUSD: { pip: 1.00, point: 0.01  },
  USOIL : { pip: 0.01, point: 0.001 },
  UKOIL : { pip: 0.01, point: 0.001 },
  EURUSD: { pip: 0.0001, point: 0.00001 },
  GBPUSD: { pip: 0.0001, point: 0.00001 },
  USDJPY: { pip: 0.01,   point: 0.001   },
};

// Normalize free-text market labels users type into the journal.
function normalizeMarket(raw) {
  if (!raw) return null;
  const s = String(raw).toUpperCase().replace(/[\s\-_/]/g, "");
  if (/^XAU(USD)?$|GOLD/.test(s)) return "XAUUSD";
  if (/^BTC(USD|USDT)?$|BITCOIN/.test(s)) return "BTCUSD";
  if (/^UK(OIL)?$|BRENT/.test(s)) return "UKOIL";
  if (/^(US)?OIL$|WTI|CRUDE/.test(s)) return "USOIL";
  if (s === "EURUSD") return "EURUSD";
  if (s === "GBPUSD") return "GBPUSD";
  if (s === "USDJPY") return "USDJPY";
  return null;
}

function fmtPips(n){
  if (!Number.isFinite(n)) return "—";
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? `${r}` : r.toFixed(1);
}
function fmtPoints(n){ return Number.isFinite(n) ? `${Math.round(n)}` : "—"; }

// Compute pip/point distance + RR for a journal entry. Returns null if the
// market symbol isn't recognized — in that case we keep the legacy R:R only.
function computeJournalDistances(entry) {
  if (!entry) return null;
  const sym = normalizeMarket(entry.market);
  if (!sym) return null;
  const cfg = PIP_POINT_SIZE[sym];
  // Reference price priority: actualEntry (executed) > entry (planned).
  const ref = parseFloat(entry.super?.actualEntry || entry.entry);
  const sl  = parseFloat(entry.sl);
  const tp  = parseFloat(entry.tp);
  if (!Number.isFinite(ref) || !Number.isFinite(sl) || !Number.isFinite(tp)) return null;
  const slDiff = Math.abs(ref - sl);
  const tpDiff = Math.abs(ref - tp);
  return {
    symbol: sym,
    reference: ref,
    referenceLabel: entry.super?.actualEntry ? "executed entry" : "planned entry",
    slPips: slDiff / cfg.pip,
    slPoints: slDiff / cfg.point,
    tpPips: tpDiff / cfg.pip,
    tpPoints: tpDiff / cfg.point,
    rr: slDiff > 0 ? tpDiff / slDiff : null,
  };
}

function calcRR(entry, sl, tp, direction) {
  const e = parseFloat(entry), s = parseFloat(sl), t = parseFloat(tp);
  if (!isFinite(e) || !isFinite(s) || !isFinite(t)) return null;
  const risk = Math.abs(e - s);
  const reward = Math.abs(t - e);
  if (risk === 0) return null;
  if (direction === "Long" && (s >= e || t <= e)) return null;
  if (direction === "Short" && (s <= e || t >= e)) return null;
  return +(reward / risk).toFixed(2);
}

// Rule-based SuPER grader. Returns "A" | "B" | "C" | "D" | "F".
function computeSuperGrade(s) {
  if (!s) return null;
  const uYes = (s.understand || []).filter(Boolean).length;
  const planComplete = (s.confluences || "").trim().split("\n").filter((l) => l.trim()).length >= 3
    && (s.invalidation || "").trim().length > 0
    && (s.timeInvalidation || "").trim().length > 0
    && (s.ifThen || "").trim().length > 0
    && !!s.setupType
    && SETUP_TYPES.includes(s.setupType);
  const riskOk = !s.riskPct || (parseFloat(s.riskPct) > 0 && parseFloat(s.riskPct) <= 2);
  const execClean = s.executionQuality === "At plan";
  const emotionClean = !["FOMO", "Revenge"].includes(s.emotion);
  const ranScan = !!s.ranScan;
  const onTopDown = !!s.onTopDown;
  const scanA = s.scanQuality === "A-grade";

  // F: revenge/FOMO emotional entry — no salvage
  if (!emotionClean) return "F";
  // D: no plan written (8 elements) or u<5 or risk > 2%
  if (!planComplete || uYes < 5 || !riskOk) return "D";
  // C: rushed scan or u<6 or chased entry
  if (!ranScan || !onTopDown || uYes < 6 || !execClean) return "C";
  // B: 1 minor deviation (scan not A-grade, or 6/7 understand)
  if (!scanA || uYes < 7) return "B";
  // A: everything clean
  return "A";
}

const GRADE_COLOR = { A: "#22C55E", B: "#C9A227", C: "#FFB347", D: "#FF6B35", F: "#FF4444" };

export default function TradingJournal() {
  const [entries, setEntries] = useState(() => flexxState.journal());
  const [form, setForm] = useState(() => defaultForm());
  const [prefillNotice, setPrefillNotice] = useState(() => {
    // Show notice if defaultForm just consumed a prefill (market non-default + notes contains 'Source:')
    if (form.notes && form.notes.startsWith("Source: ")) {
      const match = form.notes.match(/^Source: ([^·]+?)(?: ·|$)/);
      return match ? match[1].trim() : null;
    }
    return null;
  });
  const [compassPending, setCompassPending] = useState(null); // { entry, evaluation }
  const [showForm, setShowForm] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [gateResult, setGateResult] = useState(null); // {session, htf, news, account, mindset}
  const [superMode, setSuperMode] = useState(true);
  const [openSections, setOpenSections] = useState({ s: true, u: true, p: true, e: true, r: true });

  const rr = useMemo(() => calcRR(form.entry, form.sl, form.tp, form.direction), [form]);
  // Live pip / point preview for the form — uses the same authoritative table
  // as the trade-card display so the trader sees the same numbers they'll get
  // after submitting. Returns null when symbol is not in PIP_POINT_SIZE.
  const livePreview = useMemo(() => computeJournalDistances({
    market: form.market,
    entry: form.entry,
    sl: form.sl,
    tp: form.tp,
    super: { actualEntry: form.actualEntry },
  }), [form.market, form.entry, form.sl, form.tp, form.actualEntry]);
  const analytics = useMemo(() => computeAnalytics(entries), [entries]);
  const draftGrade = useMemo(
    () => (superMode ? computeSuperGrade({ ...form, executionQuality: form.executionQuality, emotion: form.emotion }) : null),
    [form, superMode]
  );
  const uYes = useMemo(() => (form.understand || []).filter(Boolean).length, [form.understand]);

  const submit = (e) => {
    e.preventDefault();
    const supr = superMode
      ? {
          ranScan: form.ranScan,
          onTopDown: form.onTopDown,
          scanQuality: form.scanQuality,
          setupType: form.setupType,
          understand: form.understand,
          uScore: uYes,
          confluences: form.confluences,
          invalidation: form.invalidation,
          timeInvalidation: form.timeInvalidation,
          ifThen: form.ifThen,
          riskPct: form.riskPct,
          actualEntry: form.actualEntry,
          executionQuality: form.executionQuality,
          grade: draftGrade,
          contextGate: gateResult,
        }
      : null;

    const entry = {
      id: Date.now().toString(),
      date: form.date,
      market: form.market,
      direction: form.direction,
      session: form.session,
      timeframe: form.timeframe,
      entry: form.entry,
      sl: form.sl,
      tp: form.tp,
      rr,
      result: form.result,
      emotion: form.emotion,
      notes: form.notes,
      isOpen: !!form.isOpen,            // new — open vs closed status
      r1Check: form.r1Check || [],      // new — R¹ active mgmt checklist
      super: supr,
      grade: supr?.grade || null,
      axelReview: null,
      axelReviewLoading: superMode && !form.isOpen,
      createdAt: Date.now(),
    };

    // Session Compass — gentle reflection, never a block. Only fires when the
    // trader has ≥3 closed trades in this session AND it's a SuPER-mode plan
    // for an OPEN trade (i.e. they're about to execute, not just logging a
    // closed trade). They can mute per-session if they don't want it.
    if (superMode && form.isOpen) {
      const evaluation = evaluateCompass(form.session, entries);
      if (evaluation) {
        setCompassPending({ entry, evaluation });
        return; // wait for the user's call inside the compass modal
      }
    }
    finalizeEntry(entry);
  };

  // Commit the entry + run the side-effects. Called either directly from
  // submit() (when no compass was needed) or from the compass "Proceed" path.
  const finalizeEntry = (entry) => {
    const supr = entry.super;
    let next = flexxState.saveJournalEntry(entry);
    setEntries(next);
    setForm(defaultForm());
    setShowForm(false);
    setGateResult(null);
    setCompassPending(null);
    awardXP(supr?.grade === "A" ? 25 : 10, supr?.grade === "A" ? "A-grade SuPER trade" : "Journal entry logged");
    checkPsychologyPattern(next);

    // Asset-quest auto-link: notify backend so Gold/BTC quests auto-tick
    // when the trader actually logs a matching trade. Fire-and-forget;
    // surfaces an XP toast via the shared `flexx:xp-updated` event so
    // the DailyQuestPanel rerenders without polling. ALSO triggers the
    // proactive AXEL push nudge when the trade graded D or F — backend
    // derives the specific 'what you skipped' reasons from the SuPER
    // diagnostics block we ship with the request.
    (async () => {
      try {
        const token = typeof window !== "undefined" ? localStorage.getItem("flexx_access_token") : null;
        const supr = entry.super || null;
        const superDiag = supr ? {
          ran_scan: !!supr.ranScan,
          on_top_down: !!supr.onTopDown,
          scan_quality: supr.scanQuality || null,
          u_score: typeof supr.uScore === "number" ? supr.uScore : (Array.isArray(supr.understand) ? supr.understand.filter(Boolean).length : null),
          confluences_count: (supr.confluences || "").split("\n").filter((l) => l.trim()).length,
          has_invalidation: !!(supr.invalidation && supr.invalidation.trim()),
          has_time_invalidation: !!(supr.timeInvalidation && supr.timeInvalidation.trim()),
          has_ifthen: !!(supr.ifThen && supr.ifThen.trim()),
          setup_type_set: !!supr.setupType,
          execution_quality: supr.executionQuality || null,
          emotion: entry.emotion || null,
        } : null;
        const res = await fetch(`${API}/api/flexx/journal/auto-link`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            pair: entry.market || "",
            session: entry.session || null,
            risk_pct: supr?.riskPct ?? null,
            grade: entry.grade || null,
            is_weekend: [0, 6].includes(new Date().getDay()),
            super: superDiag,
          }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.xp_earned > 0) {
          window.dispatchEvent(new CustomEvent("flexx:xp-updated", {
            detail: { amount: data.xp_earned, source: "journal-auto-link", quests: data.quests_completed },
          }));
        }
      } catch (e) {
        // Non-fatal — auto-link is best-effort
      }
    })();

    // Fire-and-forget AXEL review (only for SuPER-mode CLOSED trades)
    if (supr && !entry.isOpen) {
      fetchAxelReview(entry).then((review) => {
        const updated = flexxState.updateJournalEntry(entry.id, {
          axelReview: review,
          axelReviewLoading: false,
        });
        setEntries(updated);
      });
    }
  };

  const remove = (id) => {
    const next = flexxState.deleteJournalEntry(id);
    setEntries(next);
  };

  const toggleU = (i) => {
    const next = [...form.understand];
    next[i] = !next[i];
    setForm({ ...form, understand: next });
  };

  const toggleR1 = (i) => {
    const next = [...(form.r1Check || [])];
    next[i] = !next[i];
    setForm({ ...form, r1Check: next });
  };

  const handleAddTradeClick = () => {
    if (showForm) {
      // already in flow → toggle off
      setShowForm(false);
      setGateResult(null);
      setForm(defaultForm());
      return;
    }
    if (superMode) {
      // SuPER mode → MUST pass Context Gate first
      setShowGate(true);
    } else {
      setShowForm(true);
    }
  };

  const onGatePass = (summary) => {
    setGateResult(summary);
    setShowGate(false);
    setShowForm(true);
  };

  // R² weekly review (last 7 days). Spec super_2_system.md → R²-3.
  const r2 = useMemo(() => computeR2Review(entries), [entries]);

  return (
    <div data-testid="flexx-trading-journal" className="flexx-fade-in">
      <SessionsClock />
      {prefillNotice && (
        <div data-testid="journal-prefill-notice" style={{
          marginTop: 12, marginBottom: 8, padding: 12, borderRadius: 10,
          background: `${THEME.gold}10`, border: `1px solid ${THEME.gold}55`, borderLeft: `3px solid ${THEME.gold}`,
          color: THEME.text, fontSize: 13, lineHeight: 1.5, display: "flex",
          justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <span>
            <b style={{ color: THEME.gold }}>Trade prefilled</b> from {prefillNotice}.
            {" "}Pass the SuPER unlock gate to confirm the entry.
          </span>
          <button data-testid="journal-prefill-dismiss" onClick={() => setPrefillNotice(null)}
            style={{
              all: "unset", cursor: "pointer", padding: "4px 10px", borderRadius: 4,
              border: `1px solid ${THEME.gold}55`, color: THEME.mute,
              fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
            }}>DISMISS</button>
        </div>
      )}
      {compassPending && (
        <SessionCompass
          session={compassPending.evaluation.session}
          stats={compassPending.evaluation.stats}
          tone={compassPending.evaluation.tone}
          onProceed={() => finalizeEntry(compassPending.entry)}
          onHold={() => setCompassPending(null)}
          onClose={() => setCompassPending(null)}
        />
      )}
      <div className="flexx-card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>SuPER TRADE JOURNAL</div>
            <div style={{ color: THEME.text, fontSize: 20, fontWeight: 700, marginTop: 4 }}>
              {entries.length} trade{entries.length === 1 ? "" : "s"} logged
            </div>
          </div>
          <button data-testid="journal-add-btn" className="flexx-btn-primary" onClick={handleAddTradeClick}>
            {showForm ? "Cancel" : (superMode ? "⚡ RUN SuPER — New Trade" : "+ Quick Log")}
          </button>
        </div>

        {/* Gate banner inside form */}
        {showForm && superMode && gateResult && (
          <div data-testid="journal-gate-banner" style={{
            marginBottom: 14, padding: "10px 14px", borderRadius: 8,
            background: "#22C55E15", border: "1px solid #22C55E", color: "#22C55E",
            fontFamily: "monospace", fontSize: 12, letterSpacing: 1,
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
          }}>
            <span>✅ SuPER READY — All 5 Context Gates passed</span>
            <button type="button" data-testid="journal-gate-edit-btn" onClick={() => { setShowGate(true); }}
              style={{ background: "transparent", border: `1px solid #22C55E66`, color: "#22C55E", fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontFamily: "monospace" }}>Edit Gates</button>
          </div>
        )}

        {showGate && <ContextGate onPass={onGatePass} onClose={() => setShowGate(false)} />}

        {showForm && (
          <form onSubmit={submit} data-testid="journal-form">
            {/* Mode toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <button type="button" data-testid="journal-mode-super" onClick={() => setSuperMode(true)}
                style={{ ...modeBtn, ...(superMode ? modeBtnActive : {}) }}>
                ⚡ Full SuPER Log
              </button>
              <button type="button" data-testid="journal-mode-quick" onClick={() => setSuperMode(false)}
                style={{ ...modeBtn, ...(!superMode ? modeBtnActive : {}) }}>
                Quick Log
              </button>
              {superMode && draftGrade && (
                <div data-testid="journal-draft-grade" style={{
                  marginLeft: "auto", padding: "6px 14px", borderRadius: 6,
                  background: GRADE_COLOR[draftGrade] + "22", border: `1px solid ${GRADE_COLOR[draftGrade]}`,
                  color: GRADE_COLOR[draftGrade], fontWeight: 700, fontSize: 13, fontFamily: "monospace"
                }}>
                  Draft Grade: {draftGrade}
                </div>
              )}
            </div>

            {/* Trade meta */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
              <Field label="Date"><input required type="date" data-testid="journal-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inputStyle} /></Field>
              <Field label="Market / Pair"><input required type="text" data-testid="journal-market-input" value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} style={inputStyle} placeholder="EUR/USD" /></Field>
              <Field label="Direction"><select data-testid="journal-direction-select" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} style={inputStyle}>{DIRECTIONS.map((d) => <option key={d}>{d}</option>)}</select></Field>
              <Field label="Session"><select data-testid="journal-session-select" value={form.session} onChange={(e) => setForm({ ...form, session: e.target.value })} style={inputStyle}>{SESSIONS.map((d) => <option key={d}>{d}</option>)}</select></Field>
              <Field label="Timeframe"><select data-testid="journal-timeframe-select" value={form.timeframe} onChange={(e) => setForm({ ...form, timeframe: e.target.value })} style={inputStyle}>{TIMEFRAMES.map((d) => <option key={d}>{d}</option>)}</select></Field>
            </div>

            {/* SuPER sections */}
            {superMode && (
              <>
                {/* S — SCAN */}
                <SuperSection letter="S" name="SCAN" color={THEME.gold} open={openSections.s} onToggle={() => setOpenSections({ ...openSections, s: !openSections.s })}>
                  <Check label="I ran my watchlist scan today" checked={form.ranScan} onChange={(v) => setForm({ ...form, ranScan: v })} testid="super-s-ranscan" />
                  <Check label="This asset appeared on my D1→H4→H1 top-down scan" checked={form.onTopDown} onChange={(v) => setForm({ ...form, onTopDown: v })} testid="super-s-topdown" />
                  <div style={{ marginTop: 10 }}>
                    <Field label="FLEXX setup type (one of 5 only)">
                      <select required data-testid="journal-setup-type-select" value={form.setupType}
                        onChange={(e) => setForm({ ...form, setupType: e.target.value })} style={inputStyle}>
                        <option value="">-- choose one --</option>
                        {SETUP_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    {form.setupType && !SETUP_TYPES.includes(form.setupType) && (
                      <div style={{ marginTop: 6, color: "#FF4444", fontSize: 11 }}>Not a FLEXX setup. Don&apos;t take it.</div>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: THEME.mute, fontSize: 11, marginBottom: 6 }}>SETUP QUALITY</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {SCAN_QUALITY.map((q) => (
                        <button key={q} type="button" data-testid={`super-s-quality-${q}`} onClick={() => setForm({ ...form, scanQuality: q })}
                          style={{ ...pillBtn, ...(form.scanQuality === q ? pillBtnActive : {}) }}>{q}</button>
                      ))}
                    </div>
                  </div>
                </SuperSection>

                {/* u — UNDERSTAND */}
                <SuperSection letter="u" name="UNDERSTAND" color={THEME.gold} open={openSections.u} onToggle={() => setOpenSections({ ...openSections, u: !openSections.u })}
                  badge={`${uYes}/7`} badgeColor={uYes >= 5 ? "#22C55E" : "#FF6B35"}>
                  {U_QUESTIONS.map((q, i) => (
                    <Check key={i} label={q} checked={!!form.understand[i]} onChange={() => toggleU(i)} testid={`super-u-q${i}`} />
                  ))}
                  {uYes < 5 && (
                    <div data-testid="super-u-warning" style={{ marginTop: 10, padding: 10, background: "#FF6B3522", border: "1px solid #FF6B35", borderRadius: 8, color: THEME.text, fontSize: 12 }}>
                      ⚠️ <b>AXEL says:</b> Less than 5 YES answers. Are you sure about this trade? Walk away or wait for confirmation.
                    </div>
                  )}
                </SuperSection>

                {/* P — PRECISION PLAN (8 elements) */}
                <SuperSection letter="P" name="PRECISION PLAN" color={THEME.gold} open={openSections.p} onToggle={() => setOpenSections({ ...openSections, p: !openSections.p })}
                  badge="8 elements" badgeColor={THEME.gold}>
                  {/* Elements 1-3 + R:R (4-5) */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
                    <Field label="1. Entry"><input required type="number" step="any" data-testid="journal-entry-input" value={form.entry} onChange={(e) => setForm({ ...form, entry: e.target.value })} style={inputStyle} /></Field>
                    <Field label="2. Stop Loss"><input required type="number" step="any" data-testid="journal-sl-input" value={form.sl} onChange={(e) => setForm({ ...form, sl: e.target.value })} style={inputStyle} /></Field>
                    <Field label="3. Take Profit"><input required type="number" step="any" data-testid="journal-tp-input" value={form.tp} onChange={(e) => setForm({ ...form, tp: e.target.value })} style={inputStyle} /></Field>
                    <Field label="4. Risk %">
                      <input required type="number" step="0.1" min="0.1" max="2" data-testid="journal-risk-input"
                        value={form.riskPct} onChange={(e) => setForm({ ...form, riskPct: e.target.value })}
                        style={{ ...inputStyle, color: parseFloat(form.riskPct) > 2 ? "#FF4444" : THEME.text }} />
                    </Field>
                    <Field label="5. R:R (auto)">
                      <div data-testid="journal-rr-display" style={{ ...inputStyle, display: "flex", alignItems: "center",
                        color: rr == null ? THEME.mute : (rr < 2 ? "#FF6B35" : THEME.gold) }}>
                        {rr != null ? `1:${rr}${rr < 2 ? " ⚠️" : ""}` : "—"}
                      </div>
                    </Field>
                  </div>
                  {/* Live pip / point preview — only renders when market symbol is known */}
                  {livePreview && (
                    <div data-testid="journal-form-distance-preview" style={{
                      marginTop: 8, padding: "8px 11px", borderRadius: 6,
                      background: "#0d1218", border: `1px solid ${THEME.gold}33`,
                      borderLeft: `3px solid ${THEME.gold}`,
                      color: THEME.text, fontSize: 12, fontFamily: "monospace", lineHeight: 1.5,
                    }}>
                      <span style={{ color: THEME.gold, fontSize: 9, letterSpacing: 1.5 }}>DISTANCE · {livePreview.symbol}</span>
                      <div style={{ marginTop: 4 }}>
                        <span style={{ color: "#FF4444" }}>SL:</span> {fmtPips(livePreview.slPips)} pips ({fmtPoints(livePreview.slPoints)} points)
                        <span style={{ color: THEME.border, margin: "0 6px" }}>|</span>
                        <span style={{ color: "#22C55E" }}>TP:</span> {fmtPips(livePreview.tpPips)} pips ({fmtPoints(livePreview.tpPoints)} points)
                        {livePreview.rr != null && <>
                          <span style={{ color: THEME.border, margin: "0 6px" }}>|</span>
                          <span style={{ color: THEME.gold }}>RR 1:{livePreview.rr.toFixed(2)}</span>
                        </>}
                      </div>
                      <div style={{ marginTop: 2, color: THEME.mute, fontSize: 10 }}>
                        Reference: {livePreview.reference} ({livePreview.referenceLabel}) · XAU 1 pip = 0.10 · 1 point = 0.01
                      </div>
                    </div>
                  )}
                  {parseFloat(form.riskPct) > 2 && (
                    <div style={{ marginTop: 6, color: "#FF4444", fontSize: 11 }}>Risk &gt; 2% — never exceed 2%.</div>
                  )}
                  {rr != null && rr < 2 && (
                    <div style={{ marginTop: 6, color: "#FF6B35", fontSize: 11 }}>R:R below 1:2 — math doesn&apos;t work over 100 trades.</div>
                  )}
                  {/* Element 6 — Confluences (we treat 3+ confluences as the precondition) */}
                  <div style={{ marginTop: 10 }}>
                    <Field label="Confluence reasons (3+ on separate lines)">
                      <textarea rows={3} data-testid="journal-confluences-input" value={form.confluences} onChange={(e) => setForm({ ...form, confluences: e.target.value })}
                        style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} placeholder="1. D1 bullish trend\n2. H4 bounce off support\n3. RSI oversold reversal" />
                    </Field>
                  </div>
                  {/* Element 6 — Invalidation */}
                  <div style={{ marginTop: 10 }}>
                    <Field label="6. Invalidation level (PRICE that proves analysis wrong, NOT just SL)">
                      <input required type="text" data-testid="journal-invalidation-input" value={form.invalidation} onChange={(e) => setForm({ ...form, invalidation: e.target.value })} style={inputStyle} placeholder="Below 1.0820 H4 swing low" />
                    </Field>
                  </div>
                  {/* Element 7 — Time invalidation */}
                  <div style={{ marginTop: 10 }}>
                    <Field label="7. Time invalidation (when does this setup expire?)">
                      <input required type="text" data-testid="journal-time-invalidation-input" value={form.timeInvalidation} onChange={(e) => setForm({ ...form, timeInvalidation: e.target.value })}
                        style={inputStyle} placeholder="Cancel at NY open if untriggered" />
                    </Field>
                  </div>
                  {/* Element 8 — If-Then rules */}
                  <div style={{ marginTop: 10 }}>
                    <Field label="8. If-Then rules (pre-decided responses)">
                      <textarea rows={2} data-testid="journal-ifthen-input" value={form.ifThen} onChange={(e) => setForm({ ...form, ifThen: e.target.value })}
                        style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} placeholder={"If TP1 hit → SL to BE\nIf news hits → close 50%\nIf invalidation breached → exit immediately"} />
                    </Field>
                  </div>
                </SuperSection>

                {/* E — EXECUTE */}
                <SuperSection letter="E" name="EXECUTE" color={THEME.gold} open={openSections.e} onToggle={() => setOpenSections({ ...openSections, e: !openSections.e })}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                    <Field label="Actual entry price"><input type="number" step="any" data-testid="journal-actual-entry-input" value={form.actualEntry} onChange={(e) => setForm({ ...form, actualEntry: e.target.value })} style={inputStyle} /></Field>
                    <Field label="Execution quality"><select data-testid="journal-exec-quality-select" value={form.executionQuality} onChange={(e) => setForm({ ...form, executionQuality: e.target.value })} style={inputStyle}>{EXECUTION_QUALITY.map((q) => <option key={q}>{q}</option>)}</select></Field>
                    <Field label="Emotion at entry"><select data-testid="journal-emotion-select" value={form.emotion} onChange={(e) => setForm({ ...form, emotion: e.target.value })} style={inputStyle}>{EMOTIONS.map((e) => <option key={e}>{e}</option>)}</select></Field>
                  </div>
                </SuperSection>

                {/* R — REVIEW (R¹ if open, R² if closed) */}
                <SuperSection letter="R" name="REVIEW" color={THEME.gold} open={openSections.r} onToggle={() => setOpenSections({ ...openSections, r: !openSections.r })}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                    <button type="button" data-testid="journal-status-open" onClick={() => setForm({ ...form, isOpen: true })}
                      style={{ ...pillBtn, ...(form.isOpen ? pillBtnActive : {}) }}>📍 Trade still OPEN (R¹)</button>
                    <button type="button" data-testid="journal-status-closed" onClick={() => setForm({ ...form, isOpen: false })}
                      style={{ ...pillBtn, ...(!form.isOpen ? pillBtnActive : {}) }}>✅ Trade CLOSED (R²)</button>
                  </div>
                  {form.isOpen ? (
                    <div data-testid="journal-r1-block">
                      <div style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, marginBottom: 8 }}>R¹ — ACTIVE RISK MANAGEMENT</div>
                      <div style={{ color: THEME.mute, fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
                        While the trade is open, confirm you&apos;re holding to the plan. Manage from the plan, not from emotions.
                      </div>
                      {R1_CHECKLIST.map((q, i) => (
                        <Check key={i} label={q} checked={!!form.r1Check[i]} onChange={() => toggleR1(i)} testid={`super-r1-q${i}`} />
                      ))}
                    </div>
                  ) : (
                    <>
                      <div style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, marginBottom: 8 }}>R² — REVIEW LOOP</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                        <Field label="Result"><select required data-testid="journal-result-select" value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} style={inputStyle}>{RESULTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <Field label="Trade notes (lesson, what to do differently)">
                          <textarea rows={3} data-testid="journal-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} placeholder="What went right, what went wrong, what to fix…" />
                        </Field>
                      </div>
                    </>
                  )}
                </SuperSection>
              </>
            )}

            {/* Quick mode (compact form) */}
            {!superMode && (
              <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                <Field label="Entry"><input required type="number" step="any" data-testid="journal-entry-input" value={form.entry} onChange={(e) => setForm({ ...form, entry: e.target.value })} style={inputStyle} /></Field>
                <Field label="Stop Loss"><input required type="number" step="any" data-testid="journal-sl-input" value={form.sl} onChange={(e) => setForm({ ...form, sl: e.target.value })} style={inputStyle} /></Field>
                <Field label="Take Profit"><input required type="number" step="any" data-testid="journal-tp-input" value={form.tp} onChange={(e) => setForm({ ...form, tp: e.target.value })} style={inputStyle} /></Field>
                <Field label="R:R"><div style={{ ...inputStyle, color: rr != null ? THEME.gold : THEME.mute }}>{rr != null ? `1:${rr}` : "—"}</div></Field>
                <Field label="Result"><select required value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} style={inputStyle}>{RESULTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
                <Field label="Emotion"><select required value={form.emotion} onChange={(e) => setForm({ ...form, emotion: e.target.value })} style={inputStyle}>{EMOTIONS.map((e) => <option key={e}>{e}</option>)}</select></Field>
                <div style={{ gridColumn: "1 / -1" }}>
                  <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} /></Field>
                </div>
              </div>
              {livePreview && (
                <div data-testid="journal-form-distance-preview" style={{
                  marginTop: 8, padding: "8px 11px", borderRadius: 6,
                  background: "#0d1218", border: `1px solid ${THEME.gold}33`,
                  borderLeft: `3px solid ${THEME.gold}`,
                  color: THEME.text, fontSize: 12, fontFamily: "monospace", lineHeight: 1.5,
                }}>
                  <span style={{ color: THEME.gold, fontSize: 9, letterSpacing: 1.5 }}>DISTANCE · {livePreview.symbol}</span>
                  <div style={{ marginTop: 4 }}>
                    <span style={{ color: "#FF4444" }}>SL:</span> {fmtPips(livePreview.slPips)} pips ({fmtPoints(livePreview.slPoints)} points)
                    <span style={{ color: THEME.border, margin: "0 6px" }}>|</span>
                    <span style={{ color: "#22C55E" }}>TP:</span> {fmtPips(livePreview.tpPips)} pips ({fmtPoints(livePreview.tpPoints)} points)
                    {livePreview.rr != null && <>
                      <span style={{ color: THEME.border, margin: "0 6px" }}>|</span>
                      <span style={{ color: THEME.gold }}>RR 1:{livePreview.rr.toFixed(2)}</span>
                    </>}
                  </div>
                </div>
              )}
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button type="button" className="flexx-btn-ghost" onClick={() => { setShowForm(false); setForm(defaultForm()); }}>Cancel</button>
              <button type="submit" className="flexx-btn-primary" data-testid="journal-submit-btn" disabled={rr == null}>Save Trade</button>
            </div>
          </form>
        )}
      </div>

      {/* Analytics */}
      <div className="flexx-card" style={{ marginBottom: 16 }} data-testid="journal-analytics">
        <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 10 }}>ANALYTICS</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
          <StatTile label="Win Rate" value={`${analytics.winRate}%`} accent={analytics.winRate >= 45 ? THEME.green : THEME.red} />
          <StatTile label="Avg R:R" value={`1:${analytics.avgRR}`} accent={analytics.avgRR >= 1.3 ? THEME.green : THEME.mute} />
          <StatTile label="Expectancy (R)" value={analytics.expectancy} accent={parseFloat(analytics.expectancy) > 0 ? THEME.green : THEME.red} />
          <StatTile label="Trades This Week" value={analytics.thisWeek} accent={THEME.gold} />
          <StatTile label="SuPER Score" value={analytics.superScore} accent={GRADE_COLOR[analytics.superScore?.[0]] || THEME.gold} testid="analytics-super-score" />
        </div>
        <div style={{ marginTop: 12, color: THEME.mute, fontSize: 12 }}>
          Dominant emotion on wins: <b style={{ color: THEME.text }}>{analytics.winEmotion || "—"}</b> · on losses: <b style={{ color: THEME.text }}>{analytics.lossEmotion || "—"}</b>
        </div>
        {analytics.psychAlert && (
          <div data-testid="journal-psych-alert" className="flexx-fade-in" style={{ marginTop: 12, background: `${THEME.red}22`, border: `1px solid ${THEME.red}80`, color: THEME.text, padding: 12, borderRadius: 10, fontSize: 13, lineHeight: 1.5 }}>
            ⚠️ <b>AXEL — Psychology Pattern Alert</b><br />
            You've logged <b>{analytics.psychAlertState}</b> {analytics.psychAlertCount} times in the last 7 days.
            That's a signal, not a coincidence. Step back, re-read your plan, and consider halving risk until you reset.
          </div>
        )}
      </div>

      {/* R² — Weekly Review tile (Sunday ritual). Spec super_2_system.md → R²-3 */}
      {r2.weeklyTrades > 0 && (
        <div className="flexx-card" data-testid="journal-r2-weekly" style={{ marginBottom: 16, borderLeft: `3px solid ${THEME.gold}` }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 10 }}>R² — WEEKLY REVIEW (last 7 days)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            <StatTile label="Trades" value={r2.weeklyTrades} accent={THEME.gold} />
            <StatTile label="Win Rate" value={`${r2.winRate}%`} accent={r2.winRate >= 45 ? THEME.green : THEME.red} />
            <StatTile label="Avg R:R" value={`1:${r2.avgRR}`} accent={r2.avgRR >= 1.3 ? THEME.green : THEME.mute} />
            <StatTile label="Process Grade" value={r2.processGrade} accent={GRADE_COLOR[r2.processGrade?.[0]] || THEME.gold} />
            <StatTile label="Best Setup" value={r2.bestSetup || "—"} accent={THEME.text} />
          </div>
          {r2.commonMistake && (
            <div style={{ marginTop: 12, color: THEME.text, fontSize: 13, lineHeight: 1.55 }}>
              <span style={{ color: THEME.mute }}>Most-common mistake (D-grade tag): </span><b>{r2.commonMistake}</b>
            </div>
          )}

          {/* Per-session breakdown — only when we have data for >=2 sessions */}
          {(r2.bySession || []).length >= 2 && (
            <div data-testid="r2-session-breakdown" style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${THEME.border}` }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 8 }}>
                BY TRADING SESSION
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{
                  width: "100%", borderCollapse: "collapse", minWidth: 460,
                  fontFamily: "monospace", fontSize: 11,
                }}>
                  <thead>
                    <tr style={{ color: THEME.mute, fontSize: 9, letterSpacing: 1.5 }}>
                      <th style={r2Th}>SESSION</th>
                      <th style={r2Th}>TRADES</th>
                      <th style={r2Th}>W–L</th>
                      <th style={r2Th}>WIN %</th>
                      <th style={r2Th}>AVG R</th>
                      <th style={r2Th}>A-GRADE %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r2.bySession.map((s, i) => {
                      const aColor = s.aGradeRate >= 60 ? THEME.green : s.aGradeRate >= 30 ? THEME.gold : THEME.red;
                      const wColor = s.winRate >= 50 ? THEME.green : s.winRate >= 35 ? THEME.gold : THEME.red;
                      return (
                        <tr key={s.session} data-testid={`r2-session-row-${s.session.toLowerCase().replace(/ /g, "-")}`}
                          style={{ borderTop: `1px solid ${THEME.border}` }}>
                          <td style={{ ...r2Td, color: THEME.text, fontWeight: 700 }}>
                            {i === 0 ? "🏆 " : i === r2.bySession.length - 1 ? "⚠️ " : ""}{s.session}
                          </td>
                          <td style={{ ...r2Td, color: THEME.text }}>{s.trades}</td>
                          <td style={{ ...r2Td, color: THEME.text }}>{s.wins}–{s.losses}</td>
                          <td style={{ ...r2Td, color: wColor, fontWeight: 700 }}>{s.winRate}%</td>
                          <td style={{ ...r2Td, color: THEME.text }}>1:{s.avgR}</td>
                          <td style={{ ...r2Td, color: aColor, fontWeight: 700 }}>{s.aGradeRate}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {r2.sessionInsight && (
                <div data-testid="r2-session-insight" style={{
                  marginTop: 10, padding: 12, borderRadius: 8,
                  background: `${THEME.gold}10`, borderLeft: `3px solid ${THEME.gold}`,
                  color: THEME.text, fontSize: 13, lineHeight: 1.55, fontStyle: "italic",
                }}>
                  <span style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 9, letterSpacing: 2, fontStyle: "normal", marginRight: 8 }}>AXEL —</span>
                  {r2.sessionInsight}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 10, color: THEME.mute, fontSize: 12, fontStyle: "italic" }}>
            "Grade your discipline, not your profit." — pick ONE thing to do differently next week.
          </div>
        </div>
      )}

      {/* Entries list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {entries.length === 0 && (
          <div className="flexx-card" style={{ textAlign: "center", color: THEME.mute }}>
            No trades yet. Log your first trade above — it's the fastest way to improve.
          </div>
        )}
        {entries.map((e) => {
          const dist = computeJournalDistances(e);
          return (
          <div key={e.id} className="flexx-card" data-testid={`journal-entry-${e.id}`} style={{ padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {e.grade && (
                  <div data-testid={`journal-grade-${e.id}`} style={{
                    padding: "4px 10px", borderRadius: 6, fontWeight: 700, fontSize: 14,
                    background: GRADE_COLOR[e.grade] + "22", color: GRADE_COLOR[e.grade],
                    border: `1px solid ${GRADE_COLOR[e.grade]}`, fontFamily: "monospace",
                  }}>{e.grade}</div>
                )}
                {e.isOpen && (
                  <div data-testid={`journal-open-${e.id}`} style={{
                    padding: "3px 9px", borderRadius: 4, fontWeight: 700, fontSize: 10, fontFamily: "monospace", letterSpacing: 1,
                    background: "#22C55E22", color: "#22C55E", border: "1px solid #22C55E",
                  }}>● OPEN</div>
                )}
                <div>
                  <div style={{ color: THEME.text, fontWeight: 700, fontSize: 14 }}>
                    {e.market} · {e.direction} · {e.isOpen
                      ? <span style={{ color: "#22C55E" }}>OPEN</span>
                      : <span style={{ color: e.result === "Win" ? THEME.green : e.result === "Loss" ? THEME.red : THEME.gold }}>{e.result}</span>}
                  </div>
                  <div style={{ color: THEME.mute, fontSize: 11, marginTop: 3 }}>
                    {e.date} {e.session ? `· ${e.session}` : ""} {e.timeframe ? `· ${e.timeframe}` : ""} · Entry {e.entry} · SL {e.sl} · TP {e.tp} · R:R 1:{e.rr} · {e.emotion}
                    {e.super?.setupType && <> · {e.super.setupType}</>}
                    {e.super?.riskPct && <> · Risk {e.super.riskPct}%</>}
                  </div>
                  {dist && (
                    <div data-testid={`journal-distance-${e.id}`} style={{
                      color: THEME.mute, fontSize: 11, marginTop: 4, fontFamily: "monospace",
                      letterSpacing: 0.2,
                    }}>
                      <span style={{ color: THEME.red }}>SL:</span> {fmtPips(dist.slPips)} pips ({fmtPoints(dist.slPoints)} points)
                      <span style={{ color: THEME.border, margin: "0 6px" }}>|</span>
                      <span style={{ color: THEME.green }}>TP:</span> {fmtPips(dist.tpPips)} pips ({fmtPoints(dist.tpPoints)} points)
                      {dist.rr != null && <>
                        <span style={{ color: THEME.border, margin: "0 6px" }}>|</span>
                        <span style={{ color: THEME.gold }}>RR 1:{dist.rr.toFixed(2)}</span>
                      </>}
                      <span style={{ opacity: 0.55, marginLeft: 6 }}>({dist.referenceLabel})</span>
                    </div>
                  )}
                </div>
              </div>
              <button data-testid={`journal-delete-${e.id}`} className="flexx-btn-ghost" style={{ fontSize: 11, padding: "6px 10px" }} onClick={() => remove(e.id)}>Delete</button>
            </div>
            {e.notes && <div style={{ color: THEME.text, fontSize: 13, marginTop: 8, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{e.notes}</div>}
            {e.axelReviewLoading && (
              <div data-testid={`journal-axel-loading-${e.id}`} style={{ marginTop: 10, padding: 10, background: "#0d1218", border: `1px solid ${THEME.gold}40`, borderRadius: 8, color: THEME.mute, fontSize: 12 }}>
                AXEL is reviewing this trade…
              </div>
            )}
            {e.axelReview && (
              <div data-testid={`journal-axel-review-${e.id}`} style={{ marginTop: 10, padding: 12, background: `${THEME.gold}12`, border: `1px solid ${THEME.gold}50`, borderLeft: `3px solid ${THEME.gold}`, borderRadius: 8 }}>
                <div style={{ fontSize: 9, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>AXEL — TRADE REVIEW</div>
                <div style={{ color: THEME.text, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{e.axelReview}</div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

async function fetchAxelReview(entry) {
  try {
    const token = localStorage.getItem("flexx_access_token");
    if (!token || !API) return null;
    const s = entry.super || {};
    const gateLine = formatGateSummary(s.contextGate);
    const dist = computeJournalDistances(entry);
    const distLine = dist
      ? `DISTANCE: SL=${fmtPips(dist.slPips)} pips (${fmtPoints(dist.slPoints)} points) · TP=${fmtPips(dist.tpPips)} pips (${fmtPoints(dist.tpPoints)} points) · RR=1:${dist.rr?.toFixed(2) || "—"} · ref=${dist.reference} (${dist.referenceLabel}) · symbol=${dist.symbol}`
      : `DISTANCE: not computed (symbol "${entry.market}" not in pip-table)`;
    const summary = `Review my trade in ≤4 sentences. Use direct AXEL voice. Reference Layer 0 Context Gate result if any gate flagged. End with one ACTION ITEM.

Trade: ${entry.market} ${entry.direction} ${entry.session || ""} ${entry.timeframe || ""}
Entry: ${entry.entry} · SL: ${entry.sl} · TP: ${entry.tp} · R:R 1:${entry.rr}
${distLine}
Result: ${entry.result} · Emotion: ${entry.emotion}
SuPER Grade (self-computed): ${s.grade || "—"}
LAYER 0 CONTEXT GATE: ${gateLine || "(not run — pre-gate trade)"}
SCAN: ranScan=${!!s.ranScan} · topDown=${!!s.onTopDown} · quality=${s.scanQuality || "—"} · setup="${s.setupType || "—"}"
UNDERSTAND: ${s.uScore || 0}/7 questions answered YES
PLAN: confluences="${s.confluences || ""}" · risk%=${s.riskPct || "—"} · invalidation="${s.invalidation || ""}" · timeInvalidation="${s.timeInvalidation || ""}" · ifThen="${s.ifThen || ""}"
EXECUTE: actualEntry=${s.actualEntry || "—"} · executionQuality=${s.executionQuality || "—"}
Notes: ${entry.notes || ""}`;
    const res = await axios.post(
      `${API}/api/axel/chat`,
      { session_id: `trade-review-${entry.id}`, message: summary, reset: true },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
    );
    return res.data?.response || null;
  } catch (e) {
    return null;
  }
}

// Convert ContextGate raw picks → human-readable summary with pass/fail tags
// for AXEL's review prompt. Mirrors the GATES options in ContextGate.jsx.
function formatGateSummary(gate) {
  if (!gate || typeof gate !== "object") return null;
  const LABELS = {
    session: {
      London:   { label: "London",                      pass: true },
      Overlap:  { label: "London/NY Overlap",           pass: true },
      NewYork:  { label: "New York",                    pass: true },
      Asian:    { label: "Asian only",                  pass: false },
    },
    htf: {
      with:     { label: "Aligned with Daily trend",    pass: true },
      counter3: { label: "Counter-trend, 3+ confluences", pass: true },
      counter:  { label: "Counter-trend, <3 confluences", pass: false },
      unclear:  { label: "Bias unclear / chop",         pass: false },
    },
    news: {
      clear:    { label: "No red events nearby",        pass: true },
      orange:   { label: "Orange event soon",           pass: true },
      red30:    { label: "Red event within 30 min",     pass: false },
    },
    account: {
      fresh:    { label: "Healthy · within daily loss buffer", pass: true },
      won2:     { label: "Already up 2%+ today",        pass: true },
      streak:   { label: "3+ losing streak",            pass: true },
      near4:    { label: "Near 4% daily loss",          pass: false },
    },
    mindset: {
      calm:     { label: "Calm · neutral · slept enough", pass: true },
      fomo:     { label: "FOMO / fear of missing",        pass: false },
      tilt:     { label: "Reacting to last trade",        pass: false },
      tired:    { label: "Tired or stressed",             pass: false },
    },
  };
  const order = ["session", "htf", "news", "account", "mindset"];
  return order
    .map((key) => {
      const v = gate[key];
      const meta = LABELS[key]?.[v];
      if (!v || !meta) return null;
      return `${key}=${meta.label}${meta.pass ? " ✓" : " ✗FAIL"}`;
    })
    .filter(Boolean)
    .join(" | ");
}

function defaultForm() {
  const profile = flexxState.profile();
  const marketMap = { Forex: "EUR/USD", Crypto: "BTC/USDT", Stocks: "AAPL", Indices: "SPX500" };

  // Pull one-shot prefill stashed by the Tools Hub calculators (if any).
  let prefill = null;
  try {
    const raw = sessionStorage.getItem("flexx_journal_prefill");
    if (raw) {
      prefill = JSON.parse(raw);
      sessionStorage.removeItem("flexx_journal_prefill");
    }
  } catch (_) { /* ignore corrupt prefill */ }

  return {
    date: new Date().toISOString().slice(0, 10),
    market: prefill?.market || marketMap[profile.market] || "EUR/USD",
    direction: "Long",
    session: detectActiveSessionLabel(),
    timeframe: "H4",
    entry: prefill?.entry || "",
    sl: prefill?.sl || "",
    tp: prefill?.tp || "",
    result: "Win", emotion: "Confident",
    notes: prefill?.source ? `Source: ${prefill.source}${prefill.lotSize ? ` · Lot ${prefill.lotSize}` : ""}${prefill.dollarRisk ? ` · Risk $${Number(prefill.dollarRisk).toFixed(2)}` : ""}` : "",
    isOpen: false,
    r1Check: [false, false, false, false, false],
    // SuPER fields
    ranScan: false,
    onTopDown: false,
    scanQuality: "B-grade",
    setupType: "",
    understand: [false, false, false, false, false, false, false],
    confluences: "",
    invalidation: "",
    timeInvalidation: "",
    ifThen: "",
    riskPct: prefill?.riskPct || "1",
    actualEntry: "",
    executionQuality: "At plan",
  };
}

// Map the canonical SessionsClock state to the dropdown's labels in SESSIONS.
// Falls back to the most recently closed session when off-session, so the
// trader's journal is never blank-defaulted.
function detectActiveSessionLabel() {
  try {
    const { primary, previous } = getSessionState();
    if (primary?.id === "overlap-ldn-ny") return "Overlap";
    if (primary?.id === "tokyo") return "Tokyo";
    if (primary?.id === "london") return "London";
    if (primary?.id === "newyork") return "New York";
    if (previous?.id === "tokyo") return "Tokyo";
    if (previous?.id === "london") return "London";
    if (previous?.id === "newyork") return "New York";
  } catch (_) { /* fall through */ }
  return "London";
}

// R² weekly review — last 7 days summary with process grade.
function computeR2Review(entries) {
  const weekMs = 7 * 86400000;
  const now = Date.now();
  const recent = entries.filter((e) => now - (e.createdAt || 0) <= weekMs);
  if (recent.length === 0) {
    return { weeklyTrades: 0, winRate: 0, avgRR: "0.00", processGrade: "—", bestSetup: null, commonMistake: null, bySession: [], sessionInsight: null };
  }
  const closed = recent.filter((e) => !e.isOpen);
  const decided = closed.filter((e) => e.result !== "Break Even");
  const wins = decided.filter((e) => e.result === "Win");
  const winRate = decided.length ? Math.round((wins.length / decided.length) * 100) : 0;
  const avgRR = wins.length ? (wins.reduce((a, e) => a + (e.rr || 0), 0) / wins.length).toFixed(2) : "0.00";

  // Process grade — average of A/B/C/D across the week
  const graded = closed.filter((e) => e.grade);
  let processGrade = "—";
  if (graded.length > 0) {
    const map = { A: 4, B: 3, C: 2, D: 1, F: 0 };
    const inv = ["F", "D", "C", "B", "A"];
    const avg = graded.reduce((s, e) => s + (map[e.grade] ?? 0), 0) / graded.length;
    const rounded = Math.round(avg);
    processGrade = inv[rounded] + (avg - rounded >= 0.3 ? "+" : avg - rounded <= -0.3 ? "-" : "");
  }

  // Best setup — highest win-rate setup type with ≥2 closed trades
  const bySetup = {};
  closed.forEach((e) => {
    const s = e.super?.setupType;
    if (!s) return;
    bySetup[s] = bySetup[s] || { wins: 0, total: 0 };
    if (e.result === "Win") bySetup[s].wins += 1;
    if (e.result !== "Break Even") bySetup[s].total += 1;
  });
  const bestSetup = Object.entries(bySetup)
    .filter(([, v]) => v.total >= 2)
    .sort((a, b) => (b[1].wins / b[1].total) - (a[1].wins / a[1].total))[0]?.[0] || null;

  // Common mistake — most-used emotion on D/F-grade losses
  const dGrades = recent.filter((e) => ["D", "F"].includes(e.grade));
  const emoCount = {};
  dGrades.forEach((e) => { if (e.emotion) emoCount[e.emotion] = (emoCount[e.emotion] || 0) + 1; });
  const commonMistake = Object.entries(emoCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

  // Per-session breakdown — only include sessions with at least 1 trade.
  const SESSIONS_LIST = ["Tokyo", "London", "New York", "Overlap"];
  const bySession = SESSIONS_LIST.map((sess) => {
    const inSess = closed.filter((e) => e.session === sess);
    const dec = inSess.filter((e) => e.result !== "Break Even");
    const w = dec.filter((e) => e.result === "Win");
    const aRate = inSess.length ? Math.round(
      inSess.filter((e) => e.grade === "A").length / inSess.length * 100
    ) : 0;
    const wr = dec.length ? Math.round((w.length / dec.length) * 100) : 0;
    const avgR = w.length ? (w.reduce((a, e) => a + (e.rr || 0), 0) / w.length) : 0;
    return {
      session: sess,
      trades: inSess.length,
      wins: w.length,
      losses: dec.length - w.length,
      winRate: wr,
      avgR: avgR.toFixed(2),
      aGradeRate: aRate,
    };
  }).filter((s) => s.trades >= 1).sort((a, b) => b.aGradeRate - a.aGradeRate || b.winRate - a.winRate);

  // Session insight — actionable AXEL-voice line if there's a clear best/worst gap.
  let sessionInsight = null;
  if (bySession.length >= 2) {
    const eligible = bySession.filter((s) => s.trades >= 2);
    if (eligible.length >= 2) {
      const best = eligible[0], worst = eligible[eligible.length - 1];
      if (best.aGradeRate - worst.aGradeRate >= 25 || best.winRate - worst.winRate >= 25) {
        sessionInsight =
          `You're ${best.wins}-${best.losses} in ${best.session} (${best.winRate}% WR, ${best.aGradeRate}% A-grade) ` +
          `but ${worst.wins}-${worst.losses} in ${worst.session} (${worst.winRate}% WR, ${worst.aGradeRate}% A-grade). ` +
          `Stop trading what doesn't pay you.`;
      }
    }
  }

  return { weeklyTrades: recent.length, winRate, avgRR, processGrade, bestSetup, commonMistake, bySession, sessionInsight };
}

function computeAnalytics(entries) {
  if (entries.length === 0) {
    return { winRate: 0, avgRR: "0.00", expectancy: "0.00", thisWeek: 0, winEmotion: null, lossEmotion: null, psychAlert: false, superScore: "—" };
  }
  const decided = entries.filter((e) => e.result !== "Break Even");
  const wins = decided.filter((e) => e.result === "Win");
  const losses = decided.filter((e) => e.result === "Loss");
  const winRate = decided.length ? Math.round((wins.length / decided.length) * 100) : 0;
  const avgWinR = wins.length ? wins.reduce((a, e) => a + (e.rr || 0), 0) / wins.length : 0;
  const avgLossR = losses.length ? 1 : 0;
  const avgRR = wins.length ? avgWinR.toFixed(2) : "0.00";
  const expectancyVal = decided.length
    ? (wins.length / decided.length) * avgWinR - (losses.length / decided.length) * avgLossR
    : 0;
  const expectancy = expectancyVal.toFixed(2);

  const weekMs = 7 * 86400000;
  const now = Date.now();
  const recent = entries.filter((e) => now - (e.createdAt || 0) <= weekMs);
  const thisWeek = recent.length;

  const winEmotion = topByFrequency(wins.map((e) => e.emotion));
  const lossEmotion = topByFrequency(losses.map((e) => e.emotion));

  // SuPER Score: average grade across last 20 graded trades
  const graded = entries.filter((e) => e.grade).slice(0, 20);
  let superScore = "—";
  if (graded.length > 0) {
    const map = { A: 4, B: 3, C: 2, D: 1, F: 0 };
    const inv = ["F", "D", "C", "B", "A"];
    const avg = graded.reduce((s, e) => s + (map[e.grade] ?? 0), 0) / graded.length;
    const rounded = Math.round(avg);
    superScore = inv[rounded] + (avg - rounded >= 0.3 ? "+" : avg - rounded <= -0.3 ? "-" : "");
  }

  let psychAlert = false, psychAlertState = null, psychAlertCount = 0;
  ["FOMO", "Revenge"].forEach((em) => {
    const c = recent.filter((e) => e.emotion === em).length;
    if (c >= 3 && c > psychAlertCount) {
      psychAlert = true; psychAlertState = em; psychAlertCount = c;
    }
  });

  return { winRate, avgRR, expectancy, thisWeek, winEmotion, lossEmotion, psychAlert, psychAlertState, psychAlertCount, superScore };
}

function topByFrequency(arr) {
  if (arr.length === 0) return null;
  const map = {};
  arr.forEach((x) => { map[x] = (map[x] || 0) + 1; });
  return Object.entries(map).sort((a, b) => b[1] - a[1])[0][0];
}

function checkPsychologyPattern(entries) {
  const a = computeAnalytics(entries);
  if (a.psychAlert) {
    flexxState.saveFxMessage({
      role: "bot",
      message: `⚠️ I'm seeing ${a.psychAlertState} logged ${a.psychAlertCount} times in the last 7 days. That's a pattern, not an accident. Halve your risk, re-read your plan, or skip the next setup entirely.`,
      ts: Date.now(),
    });
    flexxEvents.emit("psychAlert", { state: a.psychAlertState, count: a.psychAlertCount });
  }
}

// ── UI sub-components ───────────────────────────────────────

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ color: THEME.mute, fontSize: 11, letterSpacing: 0.5, marginBottom: 6, textTransform: "uppercase" }}>{label}</div>
      {children}
    </label>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} style={{ background: "#13161c", border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ color: THEME.mute, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: accent || THEME.text, fontSize: 22, fontWeight: 800, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Check({ label, checked, onChange, testid }) {
  return (
    <label data-testid={testid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", cursor: "pointer", fontSize: 13, color: THEME.text }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 16, height: 16, accentColor: THEME.gold }} />
      <span>{label}</span>
    </label>
  );
}

function SuperSection({ letter, name, color, open, onToggle, badge, badgeColor, children }) {
  return (
    <div data-testid={`super-section-${name.toLowerCase()}`} style={{ marginBottom: 12, background: "#0d1218", border: `1px solid ${THEME.border}`, borderRadius: 10, overflow: "hidden" }}>
      <button type="button" onClick={onToggle} style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        background: "transparent", border: "none", cursor: "pointer", color: THEME.text,
      }}>
        <span style={{ fontSize: 20, fontWeight: 800, fontFamily: "monospace", color }}>{letter}</span>
        <span style={{ fontSize: 12, letterSpacing: 2, color: THEME.text, fontWeight: 700, fontFamily: "monospace" }}>{name}</span>
        {badge && (
          <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: "monospace", background: badgeColor + "22", color: badgeColor, border: `1px solid ${badgeColor}` }}>{badge}</span>
        )}
        <span style={{ marginLeft: "auto", color: THEME.mute, fontSize: 14 }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && <div style={{ padding: "0 14px 14px" }}>{children}</div>}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 10,
  background: "#13161c", border: `1px solid ${THEME.border}`, color: THEME.text, fontSize: 13,
  outline: "none", boxSizing: "border-box",
};

const modeBtn = {
  padding: "8px 14px", borderRadius: 6, background: "transparent",
  border: `1px solid ${THEME.border}`, color: THEME.mute, cursor: "pointer",
  fontSize: 12, fontWeight: 600, fontFamily: "monospace",
};
const modeBtnActive = {
  background: THEME.gold + "22", borderColor: THEME.gold, color: THEME.gold,
};
const pillBtn = {
  padding: "5px 12px", borderRadius: 14, background: "#13161c",
  border: `1px solid ${THEME.border}`, color: THEME.mute, cursor: "pointer",
  fontSize: 11, fontFamily: "monospace",
};
const pillBtnActive = {
  background: THEME.gold + "22", borderColor: THEME.gold, color: THEME.gold,
};

const r2Th = {
  textAlign: "left", padding: "8px 10px", fontWeight: 700, letterSpacing: 1.2,
  borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap",
};
const r2Td = {
  textAlign: "left", padding: "8px 10px", whiteSpace: "nowrap",
};
